import { api } from './client';
import { captureError, newTraceId } from '@/lib/telemetry';
import { holdRelease } from '@/lib/release-hold';
import type { Creative, CreativeResponse } from './types';
import type { MetaMediaValidationReport } from '@/lib/media-validation/meta-validator';

export const CREATIVE_UPLOAD_MIME_TYPES = new Set([
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'video/mp4',
  'video/quicktime',
]);

export interface LocalCreativeAsset {
  /** The original browser-selected file. Never replace this with a preview URL. */
  file: File;
  name: string;
  type: 'image' | 'video';
  url: string;
  width: number;
  height: number;
  durationMs?: number;
  validation?: MetaMediaValidationReport;
}

export interface CreativePlacementUpload {
  creativeId: string;
  placementTag: string;
  asset: LocalCreativeAsset;
  signal?: AbortSignal;
  onProgress?: (uploadedBytes: number, totalBytes: number) => void;
}

interface UploadInitResponse {
  asset_id: string;
  part_size: number;
  part_count: number;
}

/** A stalled part must fail loudly and retry rather than hang the flow forever
 *  (the 2026-08-11 CTV outage: a cross-origin part PUT sat at 0 bytes for six
 *  minutes with no timeout, no retry, and nothing in any log). */
export const PART_TIMEOUT_MS = 120_000;
const PART_RETRIES = 2;

interface UploadPart {
  part_number: number;
  etag: string;
}

interface UploadTransport {
  init(input: {
    creativeId: string;
    filename: string;
    mime: string;
    byteSize: number;
    signal?: AbortSignal;
    traceId?: string;
  }): Promise<UploadInitResponse>;
  putPart(input: {
    creativeId: string;
    assetId: string;
    partNumber: number;
    body: Blob;
    signal?: AbortSignal;
    traceId: string;
  }): Promise<string>;
  complete(input: {
    creativeId: string;
    assetId: string;
    parts: UploadPart[];
    signal?: AbortSignal;
  }): Promise<void>;
  attach(input: {
    creativeId: string;
    assetId: string;
    placementTag: string;
    width: number;
    height: number;
    durationMs?: number;
    signal?: AbortSignal;
  }): Promise<Creative>;
  abort(input: {
    creativeId: string;
    assetId: string;
  }): Promise<void>;
}

function encoded(value: string): string {
  return encodeURIComponent(value);
}

export function safeCreativeUploadFilename(name: string): string {
  const safe = name.trim().replaceAll('/', '-').replaceAll('\\', '-');
  return safe || 'creative-asset';
}

export function isSupportedCreativeMime(mime: string): boolean {
  return CREATIVE_UPLOAD_MIME_TYPES.has(mime);
}

/** One part PUT to the API's authenticated upload-part route, with a
 *  hard timeout so a stalled body fails over instead of hanging the flow. The
 *  caller's signal still cancels immediately. */
export async function putCreativePart(input: {
  creativeId: string;
  assetId: string;
  partNumber: number;
  body: Blob;
  signal?: AbortSignal;
  traceId: string;
}): Promise<string> {
  const { creativeId, assetId, partNumber, body, signal, traceId } = input;
  const timeboxed = signal
    ? AbortSignal.any([signal, AbortSignal.timeout(PART_TIMEOUT_MS)])
    : AbortSignal.timeout(PART_TIMEOUT_MS);
  const response = await api.putBinary<{ etag: string }>(
    `/creatives/${encoded(creativeId)}/upload-part/${encoded(assetId)}/${partNumber}`,
    body,
    { signal: timeboxed, headers: { 'X-Request-Id': traceId } },
  );
  const etag = response?.etag;
  if (!etag) {
    captureError({
      level: 'error',
      error_kind: 'upload_part_etag_missing',
      message: 'upload_part_etag_missing: the worker accepted a part but returned no etag',
      trace_id: traceId,
      context: { phase: 'put_part', part: partNumber, asset_id: assetId },
    });
    throw new Error('Asset part upload succeeded but the response carried no etag');
  }
  return etag;
}

const browserTransport: UploadTransport = {
  init: ({ creativeId, filename, mime, byteSize, signal, traceId }) =>
    api.post<UploadInitResponse>(
      `/creatives/${encoded(creativeId)}/upload-init`,
      {
        body: {
          filename: safeCreativeUploadFilename(filename),
          mime,
          byte_size: byteSize,
        },
        signal,
        // One trace for the whole upload: the api calls and the browser-direct
        // R2 lane all carry it, so the Logs trace chip joins them.
        ...(traceId ? { headers: { 'X-Request-Id': traceId } } : {}),
      },
    ),
  putPart: putCreativePart,
  complete: ({ creativeId, assetId, parts, signal }) =>
    api.post<void>(
      `/creatives/${encoded(creativeId)}/upload-complete`,
      { body: { asset_id: assetId, parts }, signal },
    ),
  attach: ({
    creativeId,
    assetId,
    placementTag,
    width,
    height,
    durationMs,
    signal,
  }) =>
    api.post<CreativeResponse>(
      `/creatives/${encoded(creativeId)}/assets`,
      {
        body: {
          asset_id: assetId,
          placement_tag: placementTag,
          width,
          height,
          ...(durationMs === undefined ? {} : { duration_ms: durationMs }),
        },
        signal,
      },
    ).then((response) => response.creative),
  abort: ({ creativeId, assetId }) =>
    api.post<void>(
      `/creatives/${encoded(creativeId)}/upload-abort`,
      { body: { asset_id: assetId } },
    ),
};

/**
 * Upload one browser-selected file to R2 and attach it to a creative.
 *
 * The transport argument exists so the multipart state machine can be tested
 * without making API or R2 requests. Application callers should omit it.
 */
async function uploadPlacement<T>(
  input: CreativePlacementUpload,
  transport: Omit<UploadTransport, 'attach'> & { attach: (input: Parameters<UploadTransport['attach']>[0]) => Promise<T> },
): Promise<T> {
  // Taken before the first await: a page load mid-upload strands a half-sent file, so a new
  // release waits for the whole upload, validation and attach included (2026-10-21).
  const releaseHold = holdRelease('creative_upload');
  try {
    return await uploadPlacementUnheld(input, transport);
  } finally {
    releaseHold();
  }
}

async function uploadPlacementUnheld<T>(
  input: CreativePlacementUpload,
  transport: Omit<UploadTransport, 'attach'> & { attach: (input: Parameters<UploadTransport['attach']>[0]) => Promise<T> },
): Promise<T> {
  const { asset, creativeId, placementTag, signal, onProgress } = input;
  if (!placementTag.trim()) {
    throw new Error('A placement tag is required for every creative asset');
  }
  if (!isSupportedCreativeMime(asset.file.type)) {
    throw new Error(
      `Unsupported creative MIME type: ${asset.file.type || 'unknown'}`,
    );
  }
  if (asset.file.size <= 0) {
    throw new Error('Creative asset is empty');
  }
  // What attach sends: for a Meta placement, the validator's reading of the file's own
  // bytes (the MP4 index, as the server's probe reads it), never a `<video>` element's,
  // which the server refuses when it is more than 100 ms or a pixel away.
  let measured = { width: asset.width, height: asset.height, durationMs: asset.durationMs };
  if (placementTag.startsWith('meta_')) {
    const expectedProfile = placementTag as 'meta_sq' | 'meta_fs' | 'meta_story';
    const report = asset.validation?.profileId === expectedProfile
      ? asset.validation
      : await import('@/lib/media-validation/meta-validator')
        .then(({ validateMetaCreativeFile }) =>
          validateMetaCreativeFile(asset.file, expectedProfile));
    if (!report.valid) {
      // The checks that failed, in the validator's words, so the editor that called this
      // shows what to fix rather than a sentence that names nothing.
      const failed = report.checks
        .filter((item) => item.status === 'fail')
        .map((item) => `${item.label}: ${item.actual} (expected ${item.expected})`);
      throw new Error(failed.length > 0
        ? failed.join(' · ')
        : 'Creative asset failed the required Meta media specifications');
    }
    measured = { width: report.width, height: report.height, durationMs: report.durationMs };
  }

  let assetId: string | null = null;
  // Minted before the first request so it survives even an init that never gets
  // a response — the case where no response header could carry one back.
  const traceId = newTraceId();
  let phase = 'init';
  try {
    const initialized = await transport.init({
      creativeId,
      filename: asset.file.name || asset.name,
      mime: asset.file.type,
      byteSize: asset.file.size,
      signal,
      traceId,
    });
    assetId = initialized.asset_id;

    if (initialized.part_size <= 0 || initialized.part_count <= 0) {
      throw new Error('Upload initialization returned an invalid multipart plan');
    }

    const parts: UploadPart[] = [];
    let uploadedBytes = 0;
    for (let index = 0; index < initialized.part_count; index += 1) {
      const start = index * initialized.part_size;
      const end = Math.min(start + initialized.part_size, asset.file.size);
      if (start >= end) {
        throw new Error('Upload initialization returned an invalid part boundary');
      }
      phase = 'put_part';
      const partNumber = index + 1;
      const partBody = asset.file.slice(start, end);
      // A part that stalls (timeout) or fails transiently retries in place; a
      // caller abort or a 4xx (the server named a reason) does not.
      const etag = await (async (): Promise<string> => {
        for (let attempt = 0; ; attempt += 1) {
          try {
            return await transport.putPart({
              creativeId,
              assetId: assetId as string,
              partNumber,
              body: partBody,
              signal,
              traceId,
            });
          } catch (error) {
            const callerAborted = signal?.aborted
              && error instanceof DOMException && error.name === 'AbortError';
            const status = (error as { status?: number })?.status;
            const namedRejection = typeof status === 'number' && status >= 400 && status < 500;
            if (callerAborted || namedRejection || attempt >= PART_RETRIES) throw error;
            captureError({
              level: 'warn',
              error_kind: 'upload_part_retry',
              message: `upload_part_retry: part ${partNumber} attempt ${attempt + 1} failed — `
                + `${error instanceof Error ? error.message : String(error)}`,
              trace_id: traceId,
              context: { phase: 'put_part', part: partNumber, asset_id: assetId },
            });
          }
        }
      })();
      parts.push({ part_number: partNumber, etag });
      uploadedBytes += end - start;
      onProgress?.(uploadedBytes, asset.file.size);
    }

    phase = 'complete';
    await transport.complete({
      creativeId,
      assetId,
      parts,
      signal,
    });

    phase = 'attach';
    return await transport.attach({
      creativeId,
      assetId,
      placementTag: placementTag.trim(),
      width: measured.width,
      height: measured.height,
      durationMs: measured.durationMs,
      signal,
    });
  } catch (error) {
    // One terminal line per failed attempt, whatever went wrong. This is the
    // line an operator greps for; the phase says which hop died. A cancel is
    // recorded by fetchExternal at info level and is not re-raised here as an
    // error, so the KPI does not move for user-initiated aborts.
    const aborted = error instanceof DOMException && error.name === 'AbortError';
    captureError({
      level: aborted ? 'info' : 'error',
      error_kind: aborted ? 'upload_aborted' : 'upload_flow_failed',
      message: `${aborted ? 'upload_aborted' : 'upload_flow_failed'}: ${phase} — `
        + `${error instanceof Error ? error.message : String(error)}`,
      trace_id: traceId,
      context: {
        phase,
        creative_id: creativeId,
        asset_id: assetId,
        placement_tag: placementTag.trim(),
        mime: asset.file.type,
        byte_size: asset.file.size,
      },
    });
    if (assetId) {
      try {
        await transport.abort({ creativeId, assetId });
      } catch {
        // Preserve the actionable upload/attach error. Abort is best-effort and
        // idempotent; server cleanup can retry it independently.
      }
    }
    throw error;
  }
}

export function uploadCreativePlacement(input: CreativePlacementUpload, transport: UploadTransport = browserTransport): Promise<Creative> {
  return uploadPlacement(input, transport);
}
/** Complete the upload without attaching it. The content save commits placements together. */
export function uploadCreativeFile(input: CreativePlacementUpload) {
  return uploadPlacement(input, { ...browserTransport, attach: async ({assetId, placementTag, width, height, durationMs}) => ({
    asset_id: assetId, placement_tag: placementTag, width, height, duration_ms: durationMs ?? null,
  }) });
}
